import { FolderKanban, Users } from 'lucide-react';

import {
  FILTER_OPTION_NO_VALUE,
  TableFilterConfig,
  TableFilterOption,
} from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import { getShortClients } from '@/modules/clients';
import { AvatarName, EntityAvatar, getProjectStatusOptions } from '@/modules/common';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { buildCustomAttributeFilterConfigsV2 } from '@/modules/common/utils/custom-attribute-filters';
import { getShortProjects } from '@/modules/projects';
import { renderProjectFilterLabelWithAvatar } from '@/modules/projects/helpers/project-dropdown-option';
import { ProjectBillingModel, ProjectStatus } from '@/modules/projects/types/project';
import { getShortTeamMembers } from '@/modules/team';
import {
  loadCitiesForFilter,
  loadCountriesForFilter,
  loadDepartmentsForFilter,
  loadRolesForFilter,
  loadSenioritiesForFilter,
} from '@/modules/team/api/team-filter-options.api';
import { MembershipType } from '@/modules/team/types/membership-type';

import {
  REPORT_FILTER_KEY_BILLING_MODEL,
  REPORT_FILTER_KEY_CITY,
  REPORT_FILTER_KEY_CLIENT,
  REPORT_FILTER_KEY_COUNTRY,
  REPORT_FILTER_KEY_DEPARTMENT,
  REPORT_FILTER_KEY_MEMBER,
  REPORT_FILTER_KEY_MEMBER_MANAGER,
  REPORT_FILTER_KEY_MEMBERSHIP_TYPE,
  REPORT_FILTER_KEY_PROJECT,
  REPORT_FILTER_KEY_PROJECT_MANAGER,
  REPORT_FILTER_KEY_PROJECT_STATUS,
  REPORT_FILTER_KEY_ROLE,
  REPORT_FILTER_KEY_SENIORITY,
} from '../constants/report-filter-keys';
import { ReportFilterFieldDTO } from '../types/report-filter-field';

const NS = 'authenticated.reports:filters';

const PROJECT_FIELDS = new Set<ReportFilterFieldDTO>([
  ReportFilterFieldDTO.PROJECT,
  ReportFilterFieldDTO.CLIENT,
  ReportFilterFieldDTO.PROJECT_STATUS,
  ReportFilterFieldDTO.PROJECT_MANAGER,
  ReportFilterFieldDTO.BILLING_MODEL,
]);

const renderMemberLabel = (value: string, option: TableFilterOption) => (
  <div className="flex items-center gap-2">
    <EntityAvatar image={option.avatar} name={value} size="xs" shape="circle" />
    <span className="text-sm">{value}</span>
  </div>
);

const loadMembers = (params: {
  searchString?: string;
  cursor?: number | null;
  pageSize?: number;
}) =>
  getShortTeamMembers({
    filter: { searchString: params.searchString },
    pagination: { cursor: params.cursor, pageSize: params.pageSize },
  });

const loadManagers = (params: {
  searchString?: string;
  cursor?: number | null;
  pageSize?: number;
}) =>
  getShortTeamMembers({
    filter: { searchString: params.searchString, isManager: true },
    pagination: { cursor: params.cursor, pageSize: params.pageSize },
  });

const noValueOption = (labelKey: string): TableFilterOption[] => [
  { value: FILTER_OPTION_NO_VALUE, label: i18n.t(`${NS}.${labelKey}`) },
];

interface ReportFiltersConfigOptions {
  /** Fields at least one element's data source can apply */
  availableFields: Set<ReportFilterFieldDTO>;
  /** Fields already rendered as inline pickers (driven by grouping) — kept out of the dropdown */
  inlineFields: Set<ReportFilterFieldDTO>;
  customAttributes?: CustomAttribute[];
}

/**
 * Config of the report "Filter" dropdown. Nothing shows on the canvas until the user picks a
 * filter and a value — only then does it appear as a chip.
 */
export const getReportFiltersConfig = ({
  availableFields,
  inlineFields,
  customAttributes,
}: ReportFiltersConfigOptions): TableFilterConfig[] => {
  const teamGroup = i18n.t(`${NS}.sections.team`);
  const projectGroup = i18n.t(`${NS}.sections.project`);

  const entries: Array<{ field: ReportFilterFieldDTO; configs: TableFilterConfig[] }> = [
    {
      field: ReportFilterFieldDTO.MEMBER,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_MEMBER,
          label: i18n.t(`${NS}.member`),
          withSearch: true,
          loadOptions: loadMembers,
          renderLabel: renderMemberLabel,
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.DEPARTMENT,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_DEPARTMENT,
          label: i18n.t(`${NS}.department`),
          withSearch: true,
          loadOptions: loadDepartmentsForFilter,
          topOptions: noValueOption('noDepartment'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.ROLE,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_ROLE,
          label: i18n.t(`${NS}.role`),
          withSearch: true,
          loadOptions: loadRolesForFilter,
          topOptions: noValueOption('noRole'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.SENIORITY,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_SENIORITY,
          label: i18n.t(`${NS}.seniority`),
          withSearch: true,
          loadOptions: loadSenioritiesForFilter,
          topOptions: noValueOption('noSeniority'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.MEMBER_MANAGER,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_MEMBER_MANAGER,
          label: i18n.t(`${NS}.memberManager`),
          withSearch: true,
          loadOptions: loadManagers,
          renderLabel: renderMemberLabel,
          topOptions: noValueOption('noManager'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.MEMBERSHIP_TYPE,
      configs: [
        {
          type: 'autocomplete',
          name: REPORT_FILTER_KEY_MEMBERSHIP_TYPE,
          label: i18n.t(`${NS}.membershipType`),
          options: [
            { value: MembershipType.InHouse, label: i18n.t(`${NS}.membershipTypes.inHouse`) },
            { value: MembershipType.Partner, label: i18n.t(`${NS}.membershipTypes.partner`) },
            {
              value: MembershipType.Contractor,
              label: i18n.t(`${NS}.membershipTypes.contractor`),
            },
          ],
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.LOCATION,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_COUNTRY,
          label: i18n.t(`${NS}.country`),
          withSearch: true,
          loadOptions: loadCountriesForFilter,
          topOptions: noValueOption('noCountry'),
        },
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_CITY,
          label: i18n.t(`${NS}.city`),
          withSearch: true,
          loadOptions: loadCitiesForFilter,
          dependsOn: REPORT_FILTER_KEY_COUNTRY,
          topOptions: noValueOption('noCity'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.MEMBER_CUSTOM_ATTRIBUTE,
      configs: customAttributes
        ? buildCustomAttributeFilterConfigsV2(customAttributes, 0, {
            noValueOptionLabel: i18n.t(`${NS}.noCustomAttributeValue`),
          })
        : [],
    },
    {
      field: ReportFilterFieldDTO.PROJECT,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_PROJECT,
          label: i18n.t(`${NS}.project`),
          withSearch: true,
          loadOptions: params =>
            getShortProjects({
              filter: { searchString: params.searchString },
              pagination: { cursor: params.cursor, pageSize: params.pageSize },
            }),
          renderLabel: renderProjectFilterLabelWithAvatar,
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.CLIENT,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_CLIENT,
          label: i18n.t(`${NS}.client`),
          withSearch: true,
          loadOptions: params =>
            getShortClients({
              filter: { searchString: params.searchString },
              pagination: { cursor: params.cursor, pageSize: params.pageSize },
            }),
          renderLabel: (value: string) => <AvatarName name={value} />,
          topOptions: noValueOption('noClient'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.PROJECT_STATUS,
      configs: [
        {
          type: 'autocomplete',
          name: REPORT_FILTER_KEY_PROJECT_STATUS,
          label: i18n.t(`${NS}.projectStatus`),
          displayDot: true,
          options: getProjectStatusOptions([
            ProjectStatus.Active,
            ProjectStatus.Tentative,
            ProjectStatus.Archived,
          ]),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.PROJECT_MANAGER,
      configs: [
        {
          type: 'dynamic-autocomplete',
          name: REPORT_FILTER_KEY_PROJECT_MANAGER,
          label: i18n.t(`${NS}.projectManager`),
          withSearch: true,
          loadOptions: loadManagers,
          renderLabel: renderMemberLabel,
          topOptions: noValueOption('noManager'),
        },
      ],
    },
    {
      field: ReportFilterFieldDTO.BILLING_MODEL,
      configs: [
        {
          type: 'autocomplete',
          name: REPORT_FILTER_KEY_BILLING_MODEL,
          label: i18n.t(`${NS}.billingModel`),
          options: [
            {
              value: ProjectBillingModel.TM,
              label: i18n.t(`${NS}.billingModels.timeAndMaterials`),
            },
            {
              value: ProjectBillingModel.FixedPrice,
              label: i18n.t(`${NS}.billingModels.fixedPrice`),
            },
            {
              value: ProjectBillingModel.NonBillable,
              label: i18n.t(`${NS}.billingModels.nonBillable`),
            },
          ],
        },
      ],
    },
  ];

  const isMemberField = (field: ReportFilterFieldDTO) => !PROJECT_FIELDS.has(field);

  const visible = entries.filter(e => availableFields.has(e.field) && !inlineFields.has(e.field));
  const withGroup = (e: { field: ReportFilterFieldDTO; configs: TableFilterConfig[] }) =>
    e.configs.map(c => ({ ...c, group: isMemberField(e.field) ? teamGroup : projectGroup }));

  const team = visible.filter(e => isMemberField(e.field)).flatMap(withGroup);
  const project = visible.filter(e => !isMemberField(e.field)).flatMap(withGroup);

  // groupIcon is read from the first config of each section
  if (team[0]) team[0] = { ...team[0], groupIcon: <Users /> };
  if (project[0]) project[0] = { ...project[0], groupIcon: <FolderKanban /> };

  return [...team, ...project].map((c, index) => ({ ...c, order: index }));
};
